/**
 * Group Process Preview Modal
 *
 * Shows preview for processing multiple DICOM files as a single study.
 * Uses the first file's preview data to represent the group.
 */

import { useState, useEffect, useMemo } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/presentation/components/ui/Dialog'
import { Button } from '@/presentation/components/ui/Button'
import { Badge } from '@/presentation/components/ui/Badge'
import {
  Loader2,
  UserPlus,
  User,
  FileImage,
  AlertTriangle,
  CheckCircle,
  Images,
  XCircle,
  Flag,
  Paperclip,
} from 'lucide-react'
import { get, getErrorMessage } from '@/lib/api'
import { API_ROUTES } from '@/config/constants'
import { cn } from '@/lib/utils'
import {
  formatDicomDate,
  processBatchWithAttachments,
} from '@/application/hot-folders/hotFolder.service'
import { usePriorities, useDefaultPriority } from '@/application/priorities'
import { AttachmentUploader, type AttachmentFile, prepareAttachmentsForUpload } from '../studies/AttachmentUploader'
import type { HotFolderFile } from '@/domain/hot-folder/types'

interface Patient {
  id: string
  firstName: string
  lastName: string
  document: { type: string; number: string }
  birthDate: string
}

interface Study {
  id: string
  modality: string
  studyDate: string
  studyDescription: string | null
  numberOfInstances: number
}

interface PreviewData {
  file: {
    id: string
    fileName: string
    dicomModality: string | null
  }
  patient: {
    action: 'create' | 'match'
    matchedBy?: 'document' | 'name_birthdate'
    existing?: Patient
    willCreate?: {
      firstName: string
      lastName: string
      documentNumber: string
      documentType: string
      birthDate: string
      sex: string
    }
  }
  study: {
    action: 'create' | 'add_instance' | 'duplicate'
    existing?: Study
    existingInstanceCount?: number
    willCreate?: {
      modality: string
      studyDate: string
      studyDescription: string | null
      accessionNumber: string | null
    }
  }
  warnings: string[]
}

interface GroupProcessPreviewModalProps {
  files: HotFolderFile[]
  isOpen: boolean
  onClose: () => void
  onConfirm: (result: { success: number; errors: number }) => void
}

interface ProcessResult {
  success: number
  errors: number
  details: {
    processed: Array<{ fileId: string; studyId: string | null; patientId?: string | null; isNewPatient?: boolean; isNewStudy?: boolean }>
    failed: Array<{ type: string; message: string }>
  }
}

// Translate error types to user-friendly Spanish messages
const translateErrorType = (type: string, message?: string): string => {
  // Check for specific error messages first
  if (message) {
    if (message.includes('fecha de nacimiento')) {
      return 'Falta fecha de nacimiento. Edite el archivo antes de procesar.'
    }
    if (message.includes('nombre del paciente')) {
      return 'Falta nombre del paciente. Edite el archivo antes de procesar.'
    }
    if (message.includes('duplicate key') || message.includes('ya existe')) {
      return 'Paciente duplicado en la base de datos. Contacte al administrador.'
    }
  }

  switch (type) {
    case 'already_processed':
      return 'Este archivo ya fue procesado anteriormente'
    case 'file_not_found':
      return 'Archivo no encontrado en el servidor'
    case 'patient_error':
      return message || 'Error de paciente: datos inválidos'
    case 'study_error':
      return message || 'Error de estudio: no se pudo crear'
    case 'storage_error':
      return 'Error al guardar en almacenamiento'
    case 'database_error':
      return message || 'Error de base de datos'
    default:
      return message || 'Error desconocido'
  }
}

export function GroupProcessPreviewModal({
  files,
  isOpen,
  onClose,
  onConfirm,
}: GroupProcessPreviewModalProps) {
  const [preview, setPreview] = useState<PreviewData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [processing, setProcessing] = useState(false)
  const [processResult, setProcessResult] = useState<ProcessResult | null>(null)

  // Priority state
  const [selectedPriorityId, setSelectedPriorityId] = useState<string | null>(null)
  const { data: priorities, isLoading: prioritiesLoading } = usePriorities()
  const { data: defaultPriority } = useDefaultPriority()

  // Attachments state
  const [attachments, setAttachments] = useState<AttachmentFile[]>([])

  const firstFile = files[0]
  const fileCount = files.length

  // Set default priority when loaded
  useEffect(() => {
    if (defaultPriority && !selectedPriorityId) {
      setSelectedPriorityId(defaultPriority.id)
    }
  }, [defaultPriority, selectedPriorityId])

  // Get selected priority for display
  const selectedPriority = useMemo(
    () => priorities?.find((p) => p.id === selectedPriorityId),
    [priorities, selectedPriorityId]
  )

  // Check if has any attachments (optional)
  const hasAttachments = attachments.length > 0

  // Can process if priority is selected (attachments are optional)
  const canProcess = !!selectedPriorityId

  useEffect(() => {
    if (isOpen && firstFile) {
      loadPreview()
    } else {
      setPreview(null)
      setError(null)
      setPreviewError(null)
      setProcessResult(null)
      setAttachments([])
      setSelectedPriorityId(defaultPriority?.id || null)
    }
  }, [isOpen, firstFile?.id, defaultPriority?.id])

  const loadPreview = async () => {
    if (!firstFile) return

    setLoading(true)
    setPreviewError(null)

    try {
      const data = await get<PreviewData>(API_ROUTES.HOT_FOLDER_FILES.PREVIEW(firstFile.id))
      setPreview(data)
    } catch (err: any) {
      // Preview errors are non-blocking - we can still process the files
      // The actual processing will use the DICOM data directly
      if (err?.message?.includes('insufficient') || err?.statusCode === 400) {
        setPreviewError('No se pudo obtener preview. El archivo puede estar ya procesado o tener datos incompletos.')
      } else {
        setPreviewError(err instanceof Error ? err.message : 'Error al cargar previsualizacion')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleConfirm = async () => {
    if (files.length === 0 || !canProcess || !selectedPriorityId) return

    setProcessing(true)
    setError(null)
    setProcessResult(null)

    try {
      const fileIds = files.map(f => f.id)
      const attachmentData = prepareAttachmentsForUpload(attachments)

      const result = await processBatchWithAttachments(
        fileIds,
        attachmentData,
        selectedPriorityId
      )

      console.log('[GroupProcess] Batch with attachments result:', result)

      // Convert to ProcessResult format for display
      const processResultFormatted: ProcessResult = {
        success: result.success,
        errors: result.errors,
        details: {
          processed: result.details.processed.map(p => ({
            fileId: p.fileId,
            studyId: p.studyId,
            patientId: null,
            isNewPatient: false,
            isNewStudy: p.isNewStudy,
          })),
          failed: result.details.failed,
        },
      }

      setProcessResult(processResultFormatted)

      // If all successful, close after short delay
      if (result.errors === 0 && result.success > 0) {
        setTimeout(() => {
          onConfirm(processResultFormatted)
        }, 1500)
      }
    } catch (err) {
      const errorMsg = getErrorMessage(err, 'Error al procesar archivos')
      console.error('Error processing batch:', err)
      setError(errorMsg)
    } finally {
      setProcessing(false)
    }
  }

  const handleClose = () => {
    if (processResult) {
      // Pass the result back when closing after processing
      onConfirm(processResult)
    } else {
      onClose()
    }
  }

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'Sin fecha'
    const formatted = formatDicomDate(dateStr)
    if (formatted && formatted !== 'Invalid Date') {
      return formatted
    }
    return dateStr || 'Sin fecha'
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !processing && handleClose()}>
      <DialogContent className="sm:max-w-lg md:max-w-xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Images className="h-5 w-5" />
            Cargar Estudio ({fileCount} imágenes)
          </DialogTitle>
          <DialogDescription className="text-xs">
            Se procesarán {fileCount} archivos DICOM como un solo estudio
          </DialogDescription>
        </DialogHeader>

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto min-h-0 space-y-3 pr-1">
        {loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        )}

        {error && !loading && !processing && !processResult && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-2.5">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5" />
              <div>
                <span className="text-sm font-medium text-destructive">Error</span>
                <p className="text-xs text-destructive mt-0.5">{error}</p>
              </div>
            </div>
          </div>
        )}

        {previewError && !loading && !error && !processResult && (
          <div className="rounded-lg border border-amber-500/50 bg-amber-500/10 p-2.5">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="text-xs">
                <span className="font-medium text-amber-600 dark:text-amber-400">Aviso: </span>
                <span className="text-amber-600 dark:text-amber-400">Los datos se extraerán del DICOM.</span>
              </div>
            </div>
          </div>
        )}

        {/* Show basic file info when no preview is available */}
        {!preview && !loading && previewError && !processResult && (
          <div className="space-y-2">
            {/* Files List Summary - Compact */}
            <details className="rounded-lg border bg-muted/30" open>
              <summary className="p-2 text-xs font-medium cursor-pointer hover:bg-muted/50">
                {fileCount} archivos a procesar
              </summary>
              <div className="px-2 pb-2 max-h-24 overflow-y-auto">
                <ul className="text-[10px] text-muted-foreground space-y-0.5">
                  {files.slice(0, 10).map((file) => (
                    <li key={file.id} className="font-mono flex items-center gap-1.5 truncate">
                      <FileImage className="h-3 w-3 flex-shrink-0" />
                      <span className="truncate">{file.fileName}</span>
                      {file.dicomModality && (
                        <Badge variant="outline" className="text-[9px] px-1 py-0 h-3.5 flex-shrink-0">
                          {file.dicomModality}
                        </Badge>
                      )}
                    </li>
                  ))}
                  {files.length > 10 && (
                    <li className="text-muted-foreground">... y {files.length - 10} más</li>
                  )}
                </ul>
              </div>
            </details>

            {/* Info - Compact */}
            <div className="rounded-lg p-2 flex items-center gap-2 bg-blue-500/10 border border-blue-500/50">
              <FileImage className="h-4 w-4 text-blue-500 flex-shrink-0" />
              <p className="text-xs text-blue-600 dark:text-blue-400">
                <strong>{fileCount} archivos</strong> DICOM. Datos se extraerán automáticamente.
              </p>
            </div>
          </div>
        )}

        {preview && !loading && !processResult && (
          <div className="space-y-2">
            {/* Warnings */}
            {preview.warnings.length > 0 && (
              <div className="rounded-lg border border-amber-500/50 bg-amber-500/10 p-2">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                  <span className="text-xs font-medium text-amber-600 dark:text-amber-400">
                    {preview.warnings.join(' • ')}
                  </span>
                </div>
              </div>
            )}

            {/* Patient & Study in grid on larger screens */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {/* Patient Section - Compact */}
              <div className="rounded-lg border p-2.5">
                <div className="flex items-center gap-2 mb-1.5">
                  {preview.patient.action === 'create' ? (
                    <>
                      <UserPlus className="h-4 w-4 text-green-500" />
                      <span className="text-sm font-medium">Nuevo Paciente</span>
                    </>
                  ) : (
                    <>
                      <User className="h-4 w-4 text-blue-500" />
                      <span className="text-sm font-medium">Paciente</span>
                    </>
                  )}
                </div>

                {preview.patient.action === 'create' && preview.patient.willCreate && (
                  <div className="text-xs space-y-0.5 text-muted-foreground">
                    <p className="text-foreground font-medium truncate">
                      {preview.patient.willCreate.firstName} {preview.patient.willCreate.lastName}
                    </p>
                    <p>{preview.patient.willCreate.documentType}: {preview.patient.willCreate.documentNumber}</p>
                  </div>
                )}

                {preview.patient.action === 'match' && preview.patient.existing && (
                  <div className="text-xs space-y-0.5 text-muted-foreground">
                    <p className="text-foreground font-medium truncate">
                      {preview.patient.existing.firstName} {preview.patient.existing.lastName}
                    </p>
                    <p>{preview.patient.existing.document.type}: {preview.patient.existing.document.number}</p>
                  </div>
                )}
              </div>

              {/* Study Section - Compact */}
              <div className="rounded-lg border p-2.5">
                <div className="flex items-center gap-2 mb-1.5">
                  <FileImage className="h-4 w-4 text-green-500" />
                  <span className="text-sm font-medium">Estudio</span>
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/50">
                    {fileCount} img
                  </Badge>
                </div>

                {preview.study.willCreate && (
                  <div className="text-xs space-y-0.5 text-muted-foreground">
                    <p>
                      <span className="text-foreground font-medium">{preview.study.willCreate.modality}</span>
                      {' • '}{formatDate(preview.study.willCreate.studyDate)}
                    </p>
                    {preview.study.willCreate.studyDescription && (
                      <p className="truncate">{preview.study.willCreate.studyDescription}</p>
                    )}
                  </div>
                )}

                {preview.study.existing && (
                  <div className="text-xs space-y-0.5 text-muted-foreground">
                    <p>
                      <span className="text-foreground font-medium">{preview.study.existing.modality}</span>
                      {' • '}{formatDate(preview.study.existing.studyDate)}
                    </p>
                    <p className="text-blue-600 dark:text-blue-400">+{fileCount} a {preview.study.existing.numberOfInstances} existentes</p>
                  </div>
                )}
              </div>
            </div>

            {/* Files List Summary - Compact collapsible */}
            <details className="rounded-lg border bg-muted/30">
              <summary className="p-2 text-xs font-medium cursor-pointer hover:bg-muted/50">
                {fileCount} archivos a procesar
              </summary>
              <div className="px-2 pb-2 max-h-20 overflow-y-auto">
                <ul className="text-[10px] text-muted-foreground space-y-0.5 font-mono">
                  {files.slice(0, 8).map((file) => (
                    <li key={file.id} className="truncate">• {file.fileName}</li>
                  ))}
                  {files.length > 8 && (
                    <li className="text-muted-foreground">... y {files.length - 8} más</li>
                  )}
                </ul>
              </div>
            </details>

            {/* Summary - Compact */}
            <div className={cn('rounded-lg p-2 flex items-center gap-2', 'bg-green-500/10 border border-green-500/50')}>
              <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
              <p className="text-xs text-green-600 dark:text-green-400">
                {preview.patient.action === 'create' ? 'Nuevo paciente. ' : ''}
                Estudio con <strong>{fileCount}</strong> imágenes se subirá al almacenamiento.
              </p>
            </div>
          </div>
        )}

        {/* Priority and Attachments Section - always show when not processing/result */}
        {!loading && !processResult && (
          <div className="space-y-3 border-t pt-3 mt-2">
            {/* Priority Selector - Compact */}
            <div className="flex items-center gap-2">
              <Flag className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <select
                value={selectedPriorityId || ''}
                onChange={(e) => setSelectedPriorityId(e.target.value)}
                disabled={processing || prioritiesLoading}
                className="flex-1 h-8 rounded-md border border-input bg-background px-2 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="">Seleccionar prioridad...</option>
                {priorities?.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              {selectedPriority && (
                <span
                  className="px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0"
                  style={{
                    backgroundColor: selectedPriority.color,
                    color: selectedPriority.textColor,
                  }}
                >
                  {selectedPriority.name}
                </span>
              )}
            </div>

            {/* Attachments - Compact (Optional) */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Paperclip className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Documentación Clínica</span>
                <span className="text-xs text-muted-foreground">(opcional)</span>
              </div>
              <AttachmentUploader
                attachments={attachments}
                onChange={setAttachments}
                disabled={processing}
              />
            </div>

            {/* Attachments Status Indicator - Compact */}
            {hasAttachments && (
              <div className="flex items-center gap-2 p-2 rounded-lg text-xs bg-green-500/10 border border-green-500/50 text-green-600 dark:text-green-400">
                <CheckCircle className="h-3.5 w-3.5 flex-shrink-0" />
                <span>{attachments.length} documento(s) adjunto(s)</span>
              </div>
            )}
          </div>
        )}

        {processing && (
          <div className="py-4 text-center">
            <Loader2 className="h-6 w-6 animate-spin mx-auto text-primary mb-2" />
            <p className="text-xs text-muted-foreground">
              Procesando {fileCount} archivos...
            </p>
          </div>
        )}

        {/* Process Results - Compact */}
        {processResult && !processing && (
          <div className="space-y-2">
            {/* Success Summary */}
            {processResult.success > 0 && (
              <div className="rounded-lg border border-green-500/50 bg-green-500/10 p-2.5">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                  <div className="text-xs">
                    <span className="font-medium text-green-600 dark:text-green-400">
                      {processResult.success} archivo{processResult.success > 1 ? 's' : ''} procesado{processResult.success > 1 ? 's' : ''}
                    </span>
                    {processResult.details.processed.length > 0 && (
                      <span className="text-green-600 dark:text-green-400/80 dark:text-green-400/80 ml-1">
                        • {processResult.details.processed.some(p => p.isNewStudy) ? 'Nuevo estudio' : 'Agregado a estudio'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Errors Detail */}
            {processResult.errors > 0 && (
              <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-2.5">
                <div className="flex items-center gap-2 mb-1.5">
                  <XCircle className="h-4 w-4 text-destructive flex-shrink-0" />
                  <span className="text-xs font-medium text-destructive">
                    {processResult.errors} error{processResult.errors > 1 ? 'es' : ''}
                  </span>
                </div>
                <ul className="text-xs text-destructive space-y-1 ml-6">
                  {processResult.details.failed.slice(0, 3).map((failure, idx) => (
                    <li key={idx} className="truncate">
                      • {translateErrorType(failure.type, failure.message)}
                    </li>
                  ))}
                  {processResult.details.failed.length > 3 && (
                    <li className="text-destructive/70">
                      ... y {processResult.details.failed.length - 3} más
                    </li>
                  )}
                </ul>
              </div>
            )}

            {/* All failed - special message */}
            {processResult.success === 0 && processResult.errors > 0 && (
              <div className="rounded-lg border border-amber-500/50 bg-amber-500/10 p-2">
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  <strong>Tip:</strong> Si ya fueron procesados, use "Reset" para limpiar y re-escanear.
                </p>
              </div>
            )}
          </div>
        )}
        </div>{/* End scrollable content */}

        <DialogFooter className="flex-col gap-2 flex-shrink-0 pt-3 border-t">
          {!processResult ? (
            <>
              {/* Validation Messages */}
              {!canProcess && !processing && (
                <div className="w-full text-xs text-center text-amber-600 dark:text-amber-400 space-y-1">
                  {!selectedPriorityId && <p>Seleccione una prioridad</p>}
                </div>
              )}
              <div className="flex gap-2 w-full">
                <Button variant="outline" onClick={onClose} disabled={processing} className="flex-1">
                  Cancelar
                </Button>
                <Button
                  onClick={handleConfirm}
                  disabled={loading || !!error || processing || files.length === 0 || !canProcess}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  {processing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Procesando...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Cargar Estudio
                    </>
                  )}
                </Button>
              </div>
            </>
          ) : (
            <Button onClick={handleClose} className="w-full">
              {processResult.success > 0 && processResult.errors === 0 ? (
                <>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Cerrar
                </>
              ) : (
                'Cerrar y Actualizar Lista'
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default GroupProcessPreviewModal
